/**
 * Azure OpenAI Service
 * 
 * This service provides LLM functionality using Azure OpenAI API.
 */

import { loadAzureOpenAIConfig, AzureOpenAIConfig } from '../../components/apps/office-shared/copilot/AzureOpenAIConfigDialog';
import summaryPromptText from '../../prompts/summary-prompt.txt?raw';

export class AzureOpenAIService {
  private static instance: AzureOpenAIService;

  private constructor() {}

  static getInstance(): AzureOpenAIService {
    if (!AzureOpenAIService.instance) {
      AzureOpenAIService.instance = new AzureOpenAIService();
    }
    return AzureOpenAIService.instance;
  }

  /**
   * Check if Azure OpenAI is configured
   */
  isConfigured(): boolean {
    const config = loadAzureOpenAIConfig();
    return !!(config?.apiKey && config?.endpoint);
  }

  /**
   * Get the current configuration
   */
  getConfig(): AzureOpenAIConfig | null {
    return loadAzureOpenAIConfig();
  }

  /**
   * Generate a response using Azure OpenAI
   */
  async generateResponse(prompt: string, systemPrompt?: string, maxTokens?: number): Promise<string> {
    const config = loadAzureOpenAIConfig();
    
    if (!config?.apiKey || !config?.endpoint) {
      throw new Error('Azure OpenAI is not configured');
    }

    const { apiKey, endpoint, deploymentName } = config;
    
    // Construct the API URL
    // Format: https://{resource-name}.openai.azure.com/openai/deployments/{deployment-name}/chat/completions?api-version=2024-02-15-preview
    const baseUrl = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    const apiUrl = `${baseUrl}/openai/deployments/${deploymentName}/chat/completions?api-version=2024-02-15-preview`;

    const messages = [
      {
        role: 'system',
        content: systemPrompt || 'You are a helpful AI assistant.',
      },
      {
        role: 'user',
        content: prompt,
      },
    ];

    try {
      console.log('Making Azure OpenAI request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': apiKey,
        },
        body: JSON.stringify({
          messages,
          max_tokens: maxTokens || 2000,
          temperature: 0.3,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Azure OpenAI API error:', response.status, errorText);
        throw new Error(`Azure OpenAI API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data.choices && data.choices.length > 0) {
        return data.choices[0].message.content;
      }
      
      throw new Error('No response from Azure OpenAI');
    } catch (error) {
      console.error('Azure OpenAI request failed:', error);
      throw error;
    }
  }

  /**
   * Generate a document summary
   */
  async generateSummary(documentText: string): Promise<string> {
    const systemPrompt = 'You are a helpful assistant that creates comprehensive document summaries. Be concise and direct - no filler words or unnecessary introductions.';
    // Use prompt from external txt file
    const prompt = `${summaryPromptText}
${documentText}`;
    
    return this.generateResponse(prompt, systemPrompt, 4000);
  }
}

// Export singleton instance
export const azureOpenAIService = AzureOpenAIService.getInstance();
